import { useMutation, useQuery } from '@tanstack/react-query'
import type { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query'
import type { AxiosResponse } from 'axios'

import { getAdminOrgScope } from '#/shared/api/admin-org-scope'
import type {
  DistributionUserResponse,
  GeoCodeResponse,
  PageTaskListItem,
  ShipmentCountsResponse,
  ShipmentListPage,
  ShipmentStatusCountsResponse,
  PageCarrierListItem,
  PageCustomerListItem,
  TemplateVariablesRegistry,
  PageProviderListItem,
  PageShipmentReasonResponse,
  ParsePreviewResponse,
  Provider,
  PageTagResponse,
  ProviderOption,
  TransitionCarrierRequestBody,
  UserFormPreferenceResponse,
} from './models'
import { serviceClient } from '#/shared/api/http-client'

const BASE = '/shipment/admin/api/v1/product'
/**
 * The console's non-proxied admin routes. The dispatch actions live here, not
 * under `BASE`: they are hand-written admin twins of the v1 endpoints rather
 * than proxied ones, so they hang off `/admin/api/v1` directly.
 */
const ADMIN = '/shipment/admin/api/v1'

/* eslint-disable @typescript-eslint/no-explicit-any */
type AnyResponse = AxiosResponse<any>
type QueryOpts = { query?: Partial<UseQueryOptions<AnyResponse>> & { enabled?: boolean } }
type MutationOpts<V> = { mutation?: UseMutationOptions<AnyResponse, unknown, V> }

function orgParams(extra?: Record<string, unknown>): Record<string, unknown> {
  const organization_id = getAdminOrgScope()
  return { ...(organization_id ? { organization_id } : {}), ...extra }
}

export const getGetShipmentApiV1ShipmentsShipmentIdGetQueryKey = (shipmentId: string) => [
  `/api/v1/shipments/${shipmentId}`,
]

export function useGetShipmentApiV1ShipmentsShipmentIdGet(shipmentId: string, options?: QueryOpts) {
  return useQuery<AnyResponse>({
    queryKey: getGetShipmentApiV1ShipmentsShipmentIdGetQueryKey(shipmentId),
    queryFn: () => serviceClient.get(`${BASE}/shipments/${shipmentId}`),
    ...options?.query,
  })
}

export function useUpdateShipmentApiV1ShipmentsShipmentIdPatch(
  options?: MutationOpts<{ shipmentId: string; data: any }>,
) {
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data: any }) =>
      serviceClient.patch(`${BASE}/shipments/${shipmentId}`, data),
    ...options?.mutation,
  })
}

export function useCreateShipmentApiV1ShipmentsPost(
  options?: MutationOpts<{ data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/shipments`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function convertShipmentApiV1ShipmentsShipmentIdConvertPost(
  shipmentId: string,
  params?: Record<string, unknown>,
): Promise<AnyResponse> {
  return serviceClient.post(`${BASE}/shipments/${shipmentId}/convert`, undefined, {
    params: orgParams(params),
  })
}

export function useConvertShipmentApiV1ShipmentsShipmentIdConvertPost(
  options?: MutationOpts<{ shipmentId: string; data?: any; params?: Record<string, unknown> }>,
) {
  return useMutation({
    mutationFn: ({ shipmentId, data, params }: { shipmentId: string; data?: any; params?: Record<string, unknown> }) =>
      serviceClient.post(`${BASE}/shipments/${shipmentId}/convert`, data, {
        params: orgParams(params),
      }),
    ...options?.mutation,
  })
}

export function useMarketRateApiV1ShipmentsShipmentIdMarketRateGet(
  shipmentId: string,
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/shipments/${shipmentId}/market-rate`, params],
    queryFn: () =>
      serviceClient.get(`${BASE}/shipments/${shipmentId}/market-rate`, { params }),
    ...options?.query,
  })
}

export function useMarketRateSdApiV1ShipmentsShipmentIdMarketRateSdGet(
  shipmentId: string,
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/shipments/${shipmentId}/market-rate-sd`, params],
    queryFn: () =>
      serviceClient.get(`${BASE}/shipments/${shipmentId}/market-rate-sd`, { params }),
    ...options?.query,
  })
}

export const getListShipmentsApiV1ShipmentsGetQueryKey = (params?: Record<string, unknown>) => [
  `/api/v1/shipments`,
  ...(params ? [params] : []),
]

export function useListShipmentsApiV1ShipmentsGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AxiosResponse<ShipmentListPage>>({
    queryKey: getListShipmentsApiV1ShipmentsGetQueryKey(params),
    queryFn: () => serviceClient.get(`${BASE}/shipments`, { params: orgParams(params) }),
    ...(options?.query as Partial<UseQueryOptions<AxiosResponse<ShipmentListPage>>>),
  })
}

export const getShipmentCountsApiV1ShipmentsCountsGetQueryKey = (
  params?: Record<string, unknown>,
) => [`/api/v1/shipments/counts`, ...(params ? [params] : [])]

export function useShipmentCountsApiV1ShipmentsCountsGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AxiosResponse<ShipmentCountsResponse>>({
    queryKey: getShipmentCountsApiV1ShipmentsCountsGetQueryKey(params),
    queryFn: () => serviceClient.get(`${BASE}/shipments/counts`, { params: orgParams(params) }),
    ...(options?.query as Partial<UseQueryOptions<AxiosResponse<ShipmentCountsResponse>>>),
  })
}

export const getShipmentStatusCountsApiV1ShipmentsCountsStatusesGetQueryKey = (
  params?: Record<string, unknown>,
) => [`/api/v1/shipments/counts/statuses`, ...(params ? [params] : [])]

export function useShipmentStatusCountsApiV1ShipmentsCountsStatusesGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AxiosResponse<ShipmentStatusCountsResponse>>({
    queryKey: getShipmentStatusCountsApiV1ShipmentsCountsStatusesGetQueryKey(params),
    queryFn: () =>
      serviceClient.get(`${BASE}/shipments/counts/statuses`, { params: orgParams(params) }),
    ...(options?.query as Partial<UseQueryOptions<AxiosResponse<ShipmentStatusCountsResponse>>>),
  })
}

export function getGetShipmentApiV1ShipmentsShipmentIdGetQueryOptions(
  shipmentId: string,
  options?: QueryOpts,
) {
  return {
    queryKey: getGetShipmentApiV1ShipmentsShipmentIdGetQueryKey(shipmentId),
    queryFn: () => serviceClient.get(`${BASE}/shipments/${shipmentId}`),
    ...options?.query,
  }
}

export function useListCustomersApiV1CustomersGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AxiosResponse<PageCustomerListItem>>({
    queryKey: [`/api/v1/customers`, params],
    queryFn: () => serviceClient.get(`${BASE}/customers`, { params: orgParams(params) }),
    ...(options?.query as Partial<UseQueryOptions<AxiosResponse<PageCustomerListItem>>>),
  })
}

export function useStarCustomerApiV1CustomersCustomerIdFavoritePut(
  options?: MutationOpts<{ customerId: string }>,
) {
  return useMutation({
    mutationFn: ({ customerId }: { customerId: string }) =>
      serviceClient.put(`${BASE}/customers/${customerId}/favorite`, undefined, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useUnstarCustomerApiV1CustomersCustomerIdFavoriteDelete(
  options?: MutationOpts<{ customerId: string }>,
) {
  return useMutation({
    mutationFn: ({ customerId }: { customerId: string }) =>
      serviceClient.delete(`${BASE}/customers/${customerId}/favorite`, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function checkCustomerEmailApiV1CustomersEmailCheckGet(
  params: Record<string, unknown>,
): Promise<AnyResponse> {
  return serviceClient.get(`${BASE}/customers/email-check`, { params: orgParams(params) })
}

export function getGetCustomerSummaryApiV1CustomersCustomerIdSummaryGetQueryOptions(
  customerId: string,
  options?: QueryOpts,
) {
  return {
    queryKey: [`/api/v1/customers/${customerId}/summary`],
    queryFn: () =>
      serviceClient.get(`${BASE}/customers/${customerId}/summary`, { params: orgParams() }),
    ...options?.query,
  }
}

export function useCreateCustomerApiV1CustomersPost(options?: MutationOpts<{ data: unknown }>) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/customers`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useUpdateCustomerApiV1CustomersCustomerIdPatch(
  options?: MutationOpts<{ customerId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ customerId, data }: { customerId: string; data: unknown }) =>
      serviceClient.patch(`${BASE}/customers/${customerId}`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useCreateCustomerContactApiV1CustomersCustomerIdContactsPost(
  options?: MutationOpts<{ customerId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ customerId, data }: { customerId: string; data: unknown }) =>
      serviceClient.post(`${BASE}/customers/${customerId}/contacts`, data, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function listTagsApiV1TagsGet(
  params?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<AxiosResponse<PageTagResponse>> {
  return serviceClient.get(`${BASE}/tags`, { params: orgParams(params), signal })
}

export function useCreateTagApiV1TagsPost(options?: MutationOpts<{ data: unknown }>) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/tags`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useListProvidersApiV1ProvidersGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AxiosResponse<PageProviderListItem>>({
    queryKey: [`/api/v1/providers`, params],
    queryFn: ({ signal }) => listProvidersApiV1ProvidersGet(params, signal),
    ...(options?.query as Partial<UseQueryOptions<AxiosResponse<PageProviderListItem>>>),
  })
}

export function listProvidersApiV1ProvidersGet(
  params?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<AxiosResponse<PageProviderListItem>> {
  return serviceClient.get(`${BASE}/providers`, { params: orgParams(params), signal })
}

export const getGetProviderApiV1ProvidersProviderIdGetQueryKey = (providerId: string) => [
  `/api/v1/providers/${providerId}`,
]

export function useGetProviderApiV1ProvidersProviderIdGet(
  providerId: string,
  options?: QueryOpts,
) {
  return useQuery<AxiosResponse<Provider>>({
    queryKey: getGetProviderApiV1ProvidersProviderIdGetQueryKey(providerId),
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/providers/${providerId}`, { params: orgParams(), signal }),
    ...(options?.query as Partial<UseQueryOptions<AxiosResponse<Provider>>>),
  })
}

export function useCreateProviderApiV1ProvidersPost(options?: MutationOpts<{ data: unknown }>) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/providers`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useUpdateProviderApiV1ProvidersProviderIdPatch(
  options?: MutationOpts<{ providerId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ providerId, data }: { providerId: string; data: unknown }) =>
      serviceClient.patch(`${BASE}/providers/${providerId}`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useDeleteProviderApiV1ProvidersProviderIdDelete(
  options?: MutationOpts<{ providerId: string }>,
) {
  return useMutation({
    mutationFn: ({ providerId }: { providerId: string }) =>
      serviceClient.delete(`${BASE}/providers/${providerId}`, { params: orgParams() }),
    ...options?.mutation,
  })
}

export const getGetContractTemplateApiV1ContractTemplatesTemplateIdGetQueryKey = (
  templateId: string,
) => [`/api/v1/contract-templates/${templateId}`]

export function useListContractTemplatesApiV1ContractTemplatesGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/contract-templates`, params],
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/contract-templates`, { params: orgParams(params), signal }),
    ...options?.query,
  })
}

export function useGetContractTemplateApiV1ContractTemplatesTemplateIdGet(
  templateId: string,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: getGetContractTemplateApiV1ContractTemplatesTemplateIdGetQueryKey(templateId),
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/contract-templates/${templateId}`, {
        params: orgParams(),
        signal,
      }),
    ...options?.query,
  })
}

export function useCreateContractTemplateApiV1ContractTemplatesPost(
  options?: MutationOpts<{ data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/contract-templates`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useUpdateContractTemplateApiV1ContractTemplatesTemplateIdPatch(
  options?: MutationOpts<{ templateId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ templateId, data }: { templateId: string; data: unknown }) =>
      serviceClient.patch(`${BASE}/contract-templates/${templateId}`, data, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useDeleteContractTemplateApiV1ContractTemplatesTemplateIdDelete(
  options?: MutationOpts<{ templateId: string }>,
) {
  return useMutation({
    mutationFn: ({ templateId }: { templateId: string }) =>
      serviceClient.delete(`${BASE}/contract-templates/${templateId}`, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export const getGetDistributionApiV1DistributionsDistributionIdGetQueryKey = (
  distributionId: string,
) => [`/api/v1/distributions/${distributionId}`]

export function useListDistributionsApiV1DistributionsGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/distributions`, params],
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/distributions`, { params: orgParams(params), signal }),
    ...options?.query,
  })
}

export function useGetDistributionApiV1DistributionsDistributionIdGet(
  distributionId: string,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: getGetDistributionApiV1DistributionsDistributionIdGetQueryKey(distributionId),
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/distributions/${distributionId}`, {
        params: orgParams(),
        signal,
      }),
    ...options?.query,
  })
}

export function useCreateDistributionApiV1DistributionsPost(
  options?: MutationOpts<{ data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/distributions`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useUpdateDistributionApiV1DistributionsDistributionIdPatch(
  options?: MutationOpts<{ distributionId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ distributionId, data }: { distributionId: string; data: unknown }) =>
      serviceClient.patch(`${BASE}/distributions/${distributionId}`, data, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useDeleteDistributionApiV1DistributionsDistributionIdDelete(
  options?: MutationOpts<{ distributionId: string }>,
) {
  return useMutation({
    mutationFn: ({ distributionId }: { distributionId: string }) =>
      serviceClient.delete(`${BASE}/distributions/${distributionId}`, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function parsePreviewApiV1ProvidersParsePreviewPost(
  body: unknown,
  signal?: AbortSignal,
): Promise<AxiosResponse<ParsePreviewResponse>> {
  return serviceClient.post(`${BASE}/providers/parse-preview`, body, {
    params: orgParams(),
    signal,
  })
}

export function useGeocodeLookupApiV1GeocodeGet(
  params: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AxiosResponse<GeoCodeResponse[]>>({
    queryKey: [`/api/v1/geocode`, params],
    queryFn: () => serviceClient.get(`${BASE}/geocode`, { params }),
    ...options?.query,
  })
}

export function useSearchMakesApiV1VehiclesMakesGet(
  params: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/vehicles/makes`, params],
    queryFn: () => serviceClient.get(`${BASE}/vehicles/makes`, { params }),
    ...options?.query,
  })
}

export function useSearchModelsApiV1VehiclesModelsGet(
  params: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/vehicles/models`, params],
    queryFn: () => serviceClient.get(`${BASE}/vehicles/models`, { params }),
    ...options?.query,
  })
}

export function useVinDecodeApiV1VehiclesDecodeVinVinGet(vin: string, options?: QueryOpts) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/vehicles/decode-vin/${vin}`],
    queryFn: () => serviceClient.get(`${BASE}/vehicles/decode-vin/${vin}`),
    ...options?.query,
  })
}

export function useGetFormPreferenceApiV1FormPreferencesFormTypeGet(
  _formType: string,
  options?: QueryOpts,
) {
  return useQuery<AxiosResponse<UserFormPreferenceResponse>>({
    queryKey: ['admin-form-preference', _formType],
    queryFn: () => Promise.reject(new Error('form preferences are local in the console')),
    retry: false,
    ...options?.query,
  })
}

export function useUpsertFormPreferenceApiV1FormPreferencesFormTypePut(
  options?: MutationOpts<{ formType: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: (_vars: { formType: string; data: unknown }) =>
      Promise.resolve({ data: undefined } as AxiosResponse),
    ...options?.mutation,
  })
}

export function useBackToQuoteApiV1ShipmentsShipmentIdBackToQuotePost(
  options?: MutationOpts<{ shipmentId: string; data?: unknown }>,
) {
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data?: unknown }) =>
      serviceClient.post(`${BASE}/shipments/${shipmentId}/back-to-quote`, data ?? {}),
    ...options?.mutation,
  })
}

export function useDuplicateShipmentApiV1ShipmentsShipmentIdDuplicatePost(
  options?: MutationOpts<{ shipmentId: string; data?: unknown }>,
) {
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data?: unknown }) =>
      serviceClient.post(`${BASE}/shipments/${shipmentId}/duplicate`, data ?? {}),
    ...options?.mutation,
  })
}

export function useGetCountsApiV1ToolbarCountsGet(
  params: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/toolbar/counts`, params],
    queryFn: () => serviceClient.get(`${BASE}/toolbar/counts`, { params }),
    ...options?.query,
  })
}

export function useListShipmentTeamsApiV1ShipmentsTeamsGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/shipments/teams`, params],
    queryFn: () =>
      serviceClient.get(`${BASE}/shipments/teams`, { params: orgParams(params) }),
    ...options?.query,
  })
}

export function useListReasonsApiV1ShipmentReasonsGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AxiosResponse<PageShipmentReasonResponse>>({
    queryKey: [`/api/v1/shipment-reasons`, params],
    queryFn: () =>
      serviceClient.get(`${BASE}/shipment-reasons`, { params: orgParams(params) }),
    ...(options?.query as Partial<UseQueryOptions<AxiosResponse<PageShipmentReasonResponse>>>),
  })
}

export const getListCarrierRequestsApiV1CarrierRequestsGetQueryKey = (
  params?: Record<string, unknown>,
) => [`/api/v1/carrier-requests`, ...(params ? [params] : [])]

export function useListCarrierRequestsApiV1CarrierRequestsGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/carrier-requests`, params],
    queryFn: () =>
      serviceClient.get(`${BASE}/carrier-requests`, { params: orgParams(params) }),
    ...options?.query,
  })
}

export function useTransitionCarrierRequest(
  options?: MutationOpts<{ requestId: string; data: TransitionCarrierRequestBody }>,
) {
  return useMutation({
    mutationFn: ({ requestId, data }: { requestId: string; data: TransitionCarrierRequestBody }) =>
      serviceClient.post(`${BASE}/carrier-requests/${requestId}/action`, data, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useCreateItemApiV1ToolbarPost(options?: MutationOpts<{ data: unknown }>) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/toolbar`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useUpdateItemApiV1ToolbarTypeItemIdPatch(
  options?: MutationOpts<{ type: string; itemId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ type, itemId, data }: { type: string; itemId: string; data: unknown }) =>
      serviceClient.patch(`${BASE}/toolbar/${type}/${itemId}`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useUploadNoteAttachmentApiV1ToolbarNoteAttachmentPost(
  options?: MutationOpts<{ data: { file: unknown; shipment_id: string } }>,
) {
  return useMutation({
    mutationFn: ({ data }: { data: { file: unknown; shipment_id: string } }) => {
      const formData = new FormData()
      formData.append('file', data.file as Blob)
      formData.append('shipment_id', data.shipment_id)
      return serviceClient.post(`${BASE}/toolbar/note-attachment`, formData, {
        params: orgParams(),
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    },
    ...options?.mutation,
  })
}

export function useUploadFileApiV1ToolbarUploadPost(
  options?: MutationOpts<{ data: { file: unknown; shipment_id: string } }>,
) {
  return useMutation({
    mutationFn: ({ data }: { data: { file: unknown; shipment_id: string } }) => {
      const formData = new FormData()
      formData.append('file', data.file as Blob)
      formData.append('shipment_id', data.shipment_id)
      return serviceClient.post(`${BASE}/toolbar/upload`, formData, {
        params: orgParams(),
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    },
    ...options?.mutation,
  })
}

export const getListFeedApiV1ToolbarFeedGetQueryKey = (params?: Record<string, unknown>) => [
  `/api/v1/toolbar/feed`,
  ...(params ? [params] : []),
]

export function listFeedApiV1ToolbarFeedGet(
  params?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<AnyResponse> {
  return serviceClient.get(`${BASE}/toolbar/feed`, { params: orgParams(params), signal })
}

export function useListItemsApiV1ToolbarTypeGet(
  type: string,
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/toolbar/${type}`, params],
    queryFn: () => serviceClient.get(`${BASE}/toolbar/${type}`, { params: orgParams(params) }),
    ...options?.query,
  })
}

export function previewFileApiV1ToolbarFileFileIdPreviewGet(
  fileId: string,
  params?: Record<string, unknown>,
): Promise<AnyResponse> {
  return serviceClient.get(`${BASE}/toolbar/file/${fileId}/preview`, {
    params: orgParams(params),
  })
}

export function useListContractsApiV1ContractsGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/contracts`, params],
    queryFn: () => serviceClient.get(`${BASE}/contracts`, { params: orgParams(params) }),
    ...options?.query,
  })
}

export function useSendContractApiV1ContractsContractIdSendPost(
  options?: MutationOpts<{ contractId: string; data?: unknown }>,
) {
  return useMutation({
    mutationFn: ({ contractId, data }: { contractId: string; data?: unknown }) =>
      serviceClient.post(`${BASE}/contracts/${contractId}/send`, data ?? {}, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useUpdateContractApiV1ContractsContractIdPatch(
  options?: MutationOpts<{ contractId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ contractId, data }: { contractId: string; data: unknown }) =>
      serviceClient.patch(`${BASE}/contracts/${contractId}`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useListTemplateVariablesApiV1TemplatesVariablesGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AxiosResponse<TemplateVariablesRegistry>>({
    queryKey: [`/api/v1/templates/variables`, params],
    queryFn: () =>
      serviceClient.get(`${BASE}/templates/variables`, { params: orgParams(params) }),
    ...(options?.query as Partial<UseQueryOptions<AxiosResponse<TemplateVariablesRegistry>>>),
  })
}

export function useGetTemplateContextApiV1TemplatesShipmentIdTemplateContextGet(
  shipmentId: string,
  options?: QueryOpts,
) {
  return useQuery<AxiosResponse<TemplateVariablesRegistry>>({
    queryKey: [`/api/v1/templates/${shipmentId}/template-context`],
    queryFn: () =>
      serviceClient.get(`${BASE}/templates/${shipmentId}/template-context`, {
        params: orgParams(),
      }),
    ...(options?.query as Partial<UseQueryOptions<AxiosResponse<TemplateVariablesRegistry>>>),
  })
}

export function postShipmentToLoadboardApiV1ShipmentsShipmentIdPostPost(
  shipmentId: string,
  data: unknown,
): Promise<AnyResponse> {
  return serviceClient.post(`${BASE}/shipments/${shipmentId}/post`, data)
}

export function usePostShipmentToLoadboardApiV1ShipmentsShipmentIdPostPost(
  options?: MutationOpts<{ shipmentId: string; data: { action: string; loadboards: string } }>,
) {
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data: { action: string; loadboards: string } }) =>
      serviceClient.post(`${BASE}/shipments/${shipmentId}/post`, data),
    ...options?.mutation,
  })
}

export function useCheckCustomerEmailApiV1CustomersEmailCheckGet(
  params: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/customers/email-check`, params],
    queryFn: () => checkCustomerEmailApiV1CustomersEmailCheckGet(params),
    ...options?.query,
  })
}

export function useListCarriersApiV1CarriersGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AxiosResponse<PageCarrierListItem>>({
    queryKey: [`/api/v1/carriers`, params],
    queryFn: () => serviceClient.get(`${BASE}/carriers`, { params: orgParams(params) }),
    ...(options?.query as Partial<UseQueryOptions<AxiosResponse<PageCarrierListItem>>>),
  })
}

export function useUpdateCarrierApiV1CarriersCarrierIdPatch(
  options?: MutationOpts<{ carrierId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ carrierId, data }: { carrierId: string; data: unknown }) =>
      serviceClient.patch(`${BASE}/carriers/${carrierId}`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useStarCarrierApiV1CarriersCarrierIdFavoritePut(
  options?: MutationOpts<{ carrierId: string }>,
) {
  return useMutation({
    mutationFn: ({ carrierId }: { carrierId: string }) =>
      serviceClient.put(`${BASE}/carriers/${carrierId}/favorite`, undefined, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useUnstarCarrierApiV1CarriersCarrierIdFavoriteDelete(
  options?: MutationOpts<{ carrierId: string }>,
) {
  return useMutation({
    mutationFn: ({ carrierId }: { carrierId: string }) =>
      serviceClient.delete(`${BASE}/carriers/${carrierId}/favorite`, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useUploadCarrierInsuranceApiV1CarriersCarrierIdInsurancePost(
  options?: MutationOpts<{ carrierId: string; data: { file: unknown } }>,
) {
  return useMutation({
    mutationFn: ({ carrierId, data }: { carrierId: string; data: { file: unknown } }) => {
      const formData = new FormData()
      formData.append('file', data.file as Blob)
      return serviceClient.post(`${BASE}/carriers/${carrierId}/insurance`, formData, {
        params: orgParams(),
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    },
    ...options?.mutation,
  })
}

export function useCallerLookupApiV1ShipmentsCallerGet(
  params: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/shipments/caller`, params],
    queryFn: () => serviceClient.get(`${BASE}/shipments/caller`, { params: orgParams(params) }),
    ...options?.query,
  })
}

export function useCancelShipmentDispatch(
  options?: MutationOpts<{ shipmentId: string; data?: unknown }>,
) {
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data?: unknown }) =>
      serviceClient.post(`${ADMIN}/shipments/${shipmentId}/dispatch/cancel`, data ?? {}, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useCancelShipmentActiveOffer(
  options?: MutationOpts<{ shipmentId: string; data?: unknown }>,
) {
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data?: unknown }) =>
      serviceClient.post(
        `${ADMIN}/shipments/${shipmentId}/dispatch/cancel-active-offer`,
        data ?? {},
        { params: orgParams() },
      ),
    ...options?.mutation,
  })
}

export function useUpdateSuperDispatchOrderApiV1ShipmentsShipmentIdSdUpdateOrderPost(
  options?: MutationOpts<{ shipmentId: string; data?: unknown }>,
) {
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: string; data?: unknown }) =>
      serviceClient.post(`${BASE}/shipments/${shipmentId}/sd/update-order`, data ?? {}, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useGetCarrierApiV1CarriersCarrierIdGet(carrierId: string, options?: QueryOpts) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/carriers/${carrierId}`],
    queryFn: () => serviceClient.get(`${BASE}/carriers/${carrierId}`, { params: orgParams() }),
    ...options?.query,
  })
}

export function useListTasksApiV1TasksGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AxiosResponse<PageTaskListItem>>({
    queryKey: [`/api/v1/tasks`, params],
    queryFn: () => serviceClient.get(`${BASE}/tasks`, { params: orgParams(params) }),
    ...(options?.query as Partial<UseQueryOptions<AxiosResponse<PageTaskListItem>>>),
  })
}

export function useGetCalendar(
  params: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/dashboard/calendar`, params],
    queryFn: () =>
      serviceClient.get(`${BASE}/dashboard/calendar`, { params: orgParams(params) }),
    ...options?.query,
  })
}

/**
 * `GET /shipments` as query options, for callers that `fetchQuery` a search
 * (the campaign pages open a quote by code this way).
 */
export function getListShipmentsApiV1ShipmentsGetQueryOptions(params?: Record<string, unknown>) {
  return {
    queryKey: getListShipmentsApiV1ShipmentsGetQueryKey(params),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      serviceClient.get<ShipmentListPage>(`${BASE}/shipments`, {
        params: orgParams(params),
        signal,
      }),
  }
}

const PROVIDER_OPTIONS_PAGE = 200

/**
 * The product's `GET /providers/short` — `{id, name}` for every source picker.
 * The console has no twin of the short list, so this reads the provider twin
 * it already has and keeps the two fields the pickers use.
 */
export function useListProviderOptionsApiV1ProvidersShortGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AxiosResponse<ProviderOption[]>>({
    queryKey: [`/api/v1/providers/short`, orgParams(params)],
    queryFn: async ({ signal }) => {
      const response = await listProvidersApiV1ProvidersGet(
        { size: PROVIDER_OPTIONS_PAGE, ...params },
        signal,
      )
      const items = (response.data.items ?? []).map((provider) => ({
        id: provider.id,
        name: provider.name,
      }))
      return { ...response, data: items }
    },
    ...(options?.query as Partial<UseQueryOptions<AxiosResponse<ProviderOption[]>>>),
  })
}

/**
 * The product's `GET /distributions/users` — the people a task can be given to.
 * That list is the organization's users, so the console asks auth's user twin
 * for the scoped tenant and keeps the fields the assignee picker reads.
 */
export function useListDistributionUsersApiV1DistributionsUsersGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AxiosResponse<DistributionUserResponse[]>>({
    queryKey: [`/api/v1/distributions/users`, orgParams(params)],
    queryFn: async ({ signal }) => {
      const response = await serviceClient.get<{
        items?: Array<{
          id: string
          email: string
          first_name?: string | null
          last_name?: string | null
          picture?: string | null
          is_active?: boolean
        }>
      }>('/auth/admin/api/v1/product/users', {
        params: orgParams({ size: 100, is_active: true, ...params }),
        signal,
      })
      const users = (response.data.items ?? [])
        .filter((user) => user.is_active !== false)
        .map((user) => ({
          id: user.id,
          email: user.email,
          first_name: user.first_name ?? null,
          last_name: user.last_name ?? null,
          picture: user.picture ?? null,
        }))
      return { ...response, data: users }
    },
    ...(options?.query as Partial<UseQueryOptions<AxiosResponse<DistributionUserResponse[]>>>),
  })
}
